import { Alert, Badge, Button, Group, NavLink, Paper, ScrollArea, Stack, Text, Title } from '@mantine/core'
import { IconAlertTriangle, IconClock, IconDatabase, IconPlus, IconScale, IconSettings, IconUsers } from '@tabler/icons-react'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BatchJobMonitor } from './components/BatchJobMonitor'
import { DataGenerator } from './components/DataGenerator'
import { IntegrationMonitor } from './components/IntegrationMonitor'
import { JudgeEvaluation } from './components/JudgeEvaluation'
import { UserManagement } from './components/UserManagement'

interface AdminTool {
  id: string
  title: string
  description: string
  icon: React.ComponentType<{ size?: number; stroke?: number }>
  component: React.ComponentType
  status: 'active' | 'maintenance' | 'beta'
  category: 'data' | 'integration' | 'monitoring' | 'system'
}

const ADMIN_TOOLS: AdminTool[] = [
  {
    id: 'user-management',
    title: 'User Management',
    description: 'Grant, change or remove dashboard access for ProMax and NCC staff',
    icon: IconUsers,
    component: UserManagement,
    status: 'active',
    category: 'system',
  },
  {
    id: 'data-generator',
    title: 'Data Generator',
    description: 'Generate test customers, conversations, and appointments for QA testing',
    icon: IconDatabase,
    component: DataGenerator,
    status: 'active',
    category: 'data',
  },
  {
    id: 'judge-evaluation',
    title: 'AI Agent Evaluation',
    description: 'Run simulated buyer conversations to test and score your AI sales agent',
    icon: IconScale,
    component: JudgeEvaluation,
    status: 'active',
    category: 'data',
  },
  {
    id: 'integration-monitor',
    title: 'Integration Monitor',
    description: 'Monitor and configure 3rd party integrations (ProMax, SFTP, etc.)',
    icon: IconSettings,
    component: IntegrationMonitor,
    status: 'beta',
    category: 'integration',
  },
  {
    id: 'batch-job-monitor',
    title: 'Batch Job Monitor',
    description: 'Monitor dealer inventory sync and other batch job statuses',
    icon: IconClock,
    component: BatchJobMonitor,
    status: 'active',
    category: 'monitoring',
  },
]

export function SystemAdministration() {
  const navigate = useNavigate()
  const [selectedTool, setSelectedTool] = useState<string | null>(null)

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'green'
      case 'beta':
        return 'yellow'
      case 'maintenance':
        return 'red'
      default:
        return 'gray'
    }
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'data':
        return 'blue'
      case 'integration':
        return 'green'
      case 'monitoring':
        return 'orange'
      case 'system':
        return 'red'
      default:
        return 'gray'
    }
  }

  const renderToolContent = () => {
    if (!selectedTool) {
      return (
        <Stack gap="md">
          <Alert icon={<IconAlertTriangle size={16} />} title="System Administration" color="blue">
            <Text size="sm">
              Welcome to the System Administration panel. Select a tool from the sidebar to get started. These tools are
              restricted to super administrators only.
            </Text>
          </Alert>

          <Stack gap="md">
            <Text fw={500} size="lg">
              Available Tools
            </Text>
            <Group gap="md">
              {ADMIN_TOOLS.map(tool => {
                const Icon = tool.icon
                return (
                  <Paper key={tool.id} withBorder p="md" radius="md" style={{ flex: 1, minWidth: 200 }}>
                    <Stack gap="xs">
                      <Group gap="xs">
                        <Icon size={20} stroke={1.5} />
                        <Text fw={500} size="sm">
                          {tool.title}
                        </Text>
                        <Badge size="xs" color={getStatusColor(tool.status)}>
                          {tool.status}
                        </Badge>
                      </Group>
                      <Text size="xs" c="dimmed" lineClamp={2}>
                        {tool.description}
                      </Text>
                      <Badge size="xs" variant="outline" color={getCategoryColor(tool.category)}>
                        {tool.category}
                      </Badge>
                    </Stack>
                  </Paper>
                )
              })}
            </Group>
          </Stack>
        </Stack>
      )
    }

    const tool = ADMIN_TOOLS.find(t => t.id === selectedTool)
    if (!tool) return null

    const ToolComponent = tool.component
    return <ToolComponent />
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Title order={2}>System Administration</Title>
          <Text size="sm" c="dimmed">
            Manage system integrations, monitor batch jobs, and generate test data
          </Text>
        </div>
        <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/admin/system/add-group')}>
          Add Dealership Group
        </Button>
      </Group>

      <Group align="flex-start" gap="md">
        {/* Admin Tools Navigation Sidebar */}
        <Paper shadow="sm" p="md" radius="md" withBorder style={{ width: 300 }}>
          <ScrollArea h={600}>
            <Stack gap="xs">
              <Text size="sm" fw={600} c="dimmed" tt="uppercase" mb="xs">
                Admin Tools
              </Text>
              {ADMIN_TOOLS.map(tool => {
                const IconComponent = tool.icon
                return (
                  <NavLink
                    key={tool.id}
                    active={selectedTool === tool.id}
                    label={
                      <Stack gap={0}>
                        <Group gap="xs">
                          <Text size="sm" fw={500}>
                            {tool.title}
                          </Text>
                          <Badge size="xs" color={getStatusColor(tool.status)}>
                            {tool.status}
                          </Badge>
                        </Group>
                        <Text size="xs" c="dimmed" lineClamp={2}>
                          {tool.description}
                        </Text>
                      </Stack>
                    }
                    leftSection={<IconComponent size={20} stroke={1.5} />}
                    onClick={() => setSelectedTool(tool.id)}
                  />
                )
              })}
            </Stack>
          </ScrollArea>
        </Paper>

        {/* Tool Content */}
        <div style={{ flex: 1 }}>
          <Stack gap="md">
            {selectedTool && (
              <Paper shadow="sm" p="md" radius="md" withBorder>
                <Group justify="space-between">
                  <div>
                    <Group gap="xs">
                      {(() => {
                        const tool = ADMIN_TOOLS.find(t => t.id === selectedTool)
                        if (!tool) return null
                        const IconComponent = tool.icon
                        return (
                          <>
                            <IconComponent size={24} stroke={1.5} />
                            <Title order={3}>{tool.title}</Title>
                          </>
                        )
                      })()}
                    </Group>
                    <Text size="sm" c="dimmed">
                      {ADMIN_TOOLS.find(t => t.id === selectedTool)?.description}
                    </Text>
                  </div>
                  <Badge
                    color={getCategoryColor(ADMIN_TOOLS.find(t => t.id === selectedTool)?.category || 'system')}
                    variant="light"
                  >
                    {ADMIN_TOOLS.find(t => t.id === selectedTool)?.category}
                  </Badge>
                </Group>

                {/* Tool-specific alerts */}
                {selectedTool === 'integration-monitor' && (
                  <Alert icon={<IconAlertTriangle size={16} />} title="Beta Feature" color="yellow" mt="md">
                    This feature is currently in beta. Some integrations may not be fully functional.
                  </Alert>
                )}
              </Paper>
            )}

            {renderToolContent()}
          </Stack>
        </div>
      </Group>
    </Stack>
  )
}
